import { marked } from 'marked'

const HEADING_PATTERN = /^(#{1,6})\s+(.*)$/
const BULLET_PATTERN = /^[-*]\s+(.*)$/
const NUMBERED_PATTERN = /^(\d{1,3})[.)]\s+(.*)$/

export interface PlanLineRender {
  html: string
  kind: 'heading' | 'bullet' | 'numbered' | 'text'
  depth?: number
}

/**
 * PlanModal splits plan.content on '\n' and renders each line independently so comments can
 * anchor to a lineIndex. marked.parse (block mode) is wrong here — parsing one line at a time as
 * a standalone document restarts every numbered list at 1 — so this only ever calls
 * marked.parseInline, after first peeling off a leading heading, bullet, or numbered-list marker
 * so headings and bullets still render as such instead of literal '#'/'-' characters. The typed
 * digit of a numbered marker ("1.", "2)") is stripped from the html entirely — PlanModal derives
 * the displayed number from the line's position instead, so a duplicate or out-of-order digit in
 * the plan's raw content never reaches the reader. This is display-only: the raw line (including
 * its original digit) still flows to the drafting agent and the verbatim revision rule untouched.
 */
export function renderPlanLineHtml(line: string): PlanLineRender {
  const headingMatch = line.match(HEADING_PATTERN)
  if (headingMatch) {
    return {
      html: marked.parseInline(headingMatch[2]) as string,
      kind: 'heading',
      depth: headingMatch[1].length
    }
  }

  const bulletMatch = line.match(BULLET_PATTERN)
  if (bulletMatch) {
    return {
      html: marked.parseInline(bulletMatch[1]) as string,
      kind: 'bullet'
    }
  }

  const numberedMatch = line.match(NUMBERED_PATTERN)
  if (numberedMatch) {
    return {
      html: marked.parseInline(numberedMatch[2]) as string,
      kind: 'numbered'
    }
  }

  return {
    html: marked.parseInline(line) as string,
    kind: 'text'
  }
}
